import { useMemo, useState } from 'react'
import { Loader2 } from 'lucide-react'
import { usePortal } from '../contexts/PortalContext'
import { Button } from './ui/button'
import { Card, CardContent } from './ui/card'
import { Checkbox } from './ui/checkbox'
import { cn } from '../lib/utils'

export function SupplierWelcomeScreen() {
  const { data, isLoading, error, startForm, isComplementMode, requestedFieldIdsSet } = usePortal()
  const [consentChecked, setConsentChecked] = useState(false)
  const [isStarting, setIsStarting] = useState(false)

  // Story 3.3 AC #8 / AC #11 — résoudre les labels des champs demandés :
  //  - filtrage des IDs orphelins (champs supprimés du template entre la
  //    demande et l'accès) — déjà appliqué dans requestedFieldIdsSet (cf.
  //    PortalContext) qui ne contient que les IDs résolvables.
  //  - dédup pour éviter qu'un même label apparaisse deux fois si la demande
  //    contenait des doublons (donnée dégradée mais possible).
  //  - clé React = fieldId stable, pas l'index (évite les warnings de réordo).
  const requestedFieldEntries = useMemo<{ id: string; label: string }[]>(() => {
    if (!data?.complementRequest) return []
    if (!Array.isArray(data.complementRequest.requestedFieldIds)) return []
    const fieldById = new Map<string, string>()
    for (const section of data.template.sections) {
      for (const f of section.fields) fieldById.set(f.id, f.label)
    }
    const seen = new Set<string>()
    const out: { id: string; label: string }[] = []
    for (const id of data.complementRequest.requestedFieldIds) {
      if (typeof id !== 'string' || seen.has(id)) continue
      if (!requestedFieldIdsSet.has(id)) continue // filtré orphan
      seen.add(id)
      out.push({ id, label: fieldById.get(id) ?? id })
    }
    return out
  }, [data, requestedFieldIdsSet])

  if (!data) return null

  const { organization, campaign, complementRequest } = data
  const requestedCount = requestedFieldEntries.length

  const handleStart = async () => {
    // Story 3.3 AC #8 — pas de checkbox consent en mode complément → CTA directement activable
    if ((!isComplementMode && !consentChecked) || isStarting) return
    setIsStarting(true)
    await startForm()
    setIsStarting(false)
  }

  return (
    <Card className="w-full max-w-lg mx-auto border shadow-lg" style={{ borderColor: 'var(--supplier-border)' }}>
      <CardContent className="p-8 space-y-6">
        {/* En-tête organisation */}
        <div className="text-center space-y-2">
          <div
            className="inline-flex items-center justify-center w-14 h-14 rounded-full text-white font-bold text-xl mb-2"
            style={{ backgroundColor: 'var(--supplier-primary-strong)' }}
          >
            {organization.name.charAt(0).toUpperCase()}
          </div>
          <h1 className="text-xl font-semibold" style={{ color: 'var(--supplier-text)' }}>
            {organization.name}
          </h1>
        </div>

        {/* Titre campagne — adapté au mode complément (Story 3.3 AC #8) */}
        <div className="text-center space-y-1">
          <h2 className="text-2xl font-bold" style={{ color: 'var(--supplier-text)' }}>
            {isComplementMode ? `Complément demandé — ${campaign.name}` : campaign.name}
          </h2>
          <p className="text-sm text-muted-foreground">
            {isComplementMode
              ? `${organization.name} a besoin d'informations complémentaires (${requestedCount} champ${requestedCount > 1 ? 's' : ''})`
              : 'Durée estimée : ~15 min'}
          </p>
          {isComplementMode && (
            <p className="text-xs text-muted-foreground">Environ 2 minutes</p>
          )}
        </div>

        {/* Instructions — uniquement en mode initial */}
        {!isComplementMode && (
          <p className="text-sm text-center text-muted-foreground leading-relaxed">
            Merci de remplir ce formulaire environnemental. Vos réponses permettront à{' '}
            <span className="font-medium" style={{ color: 'var(--supplier-text)' }}>
              {organization.name}
            </span>{' '}
            de calculer l'empreinte carbone de sa chaîne d'approvisionnement.
          </p>
        )}

        {/* Story 3.3 AC #8 — Bloc message {requestedBy} (mode complément uniquement, si message non-null) */}
        {isComplementMode && complementRequest?.message && (
          <blockquote
            className="rounded-r-md border-l-4 p-4 text-sm leading-relaxed"
            style={{
              backgroundColor: 'var(--supplier-accent)',
              borderLeftColor: 'var(--supplier-primary)',
              color: 'var(--supplier-text)',
            }}
          >
            <p className="whitespace-pre-wrap">{complementRequest.message}</p>
            <footer className="mt-2 text-xs text-muted-foreground">
              — Message de {complementRequest.requestedBy}
            </footer>
          </blockquote>
        )}

        {/* Story 3.3 AC #8 — Liste des champs à compléter (mode complément) */}
        {isComplementMode && requestedFieldEntries.length > 0 && (
          <div className="space-y-2">
            <h3 className="text-sm font-semibold" style={{ color: 'var(--supplier-text)' }}>
              Champs à compléter
            </h3>
            <ul className="list-disc pl-5 space-y-1 text-sm" style={{ color: 'var(--supplier-text)' }}>
              {requestedFieldEntries.map((entry) => (
                <li key={entry.id}>{entry.label}</li>
              ))}
            </ul>
          </div>
        )}

        {/* Consentement RGPD — CA-5 — uniquement en mode initial (Story 3.3 AC #8 : déjà recueilli au tour 1) */}
        {!isComplementMode && (
          <div
            className={cn(
              'flex items-start gap-3 rounded-lg border p-4',
              consentChecked ? 'border-[var(--supplier-primary)]' : 'border-[var(--supplier-border)]'
            )}
            style={{ backgroundColor: 'var(--supplier-accent)' }}
          >
            <Checkbox
              id="rgpd-consent"
              checked={consentChecked}
              onCheckedChange={(checked) => setConsentChecked(checked === true)}
              className="mt-0.5"
            />
            <label
              htmlFor="rgpd-consent"
              className="text-sm leading-relaxed cursor-pointer select-none"
              style={{ color: 'var(--supplier-text)' }}
            >
              J'accepte la collecte et le traitement de mes données environnementales par{' '}
              <span className="font-semibold">{organization.name}</span>
            </label>
          </div>
        )}

        {/* Message d'erreur consent — H2 : affiché si recordConsent échoue */}
        {error && (
          <p className="text-sm text-red-600 text-center" role="alert">
            {error}
          </p>
        )}

        {/* Bouton CTA — CA-5 : désactivé tant que RGPD non coché (mode initial) ; directement activable en mode complément */}
        <Button
          className="w-full text-base font-semibold min-h-[48px]"
          style={{
            backgroundColor: isComplementMode || consentChecked ? 'var(--supplier-primary-strong)' : undefined,
          }}
          disabled={(!isComplementMode && !consentChecked) || isStarting || isLoading}
          onClick={handleStart}
        >
          {isStarting ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Chargement…
            </>
          ) : isComplementMode ? (
            'Compléter ma réponse'
          ) : (
            'Commencer'
          )}
        </Button>
      </CardContent>
    </Card>
  )
}
